import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import Plus from 'components/ReusableComponents/Messaging/NoPWABox/Plus'
import useForm from 'hooks/useForm'
import { useEffect, useState } from 'react'
import dashboardApi from 'services/api/dashboard'

import {
  behaviorNames,
  directBlacklistCategories as blCats,
  indieMarketScopeNames,
  indieScopeNames,
  marketScopeNames,
  scopeNames
} from '../../../main/blacklistHelpers'
import ConfirmationDialog from '../ConfirmationDialog/index'
import styles from './AddBlacklistRule.module.scss'

function getOptions({ isIndependent, requestCategory }) {
  if (isIndependent && requestCategory === 'marketCodeFull') {
    return indieMarketScopeNames
  }

  if (isIndependent) {
    return indieScopeNames
  }

  if (requestCategory === 'marketCodeFull') {
    return marketScopeNames
  }

  return scopeNames
}

export const AddMultiBlacklistRule = ({
  handleConfirm,
  handleClose,
  itemValues = [],
  requestCategory = 'Unknown'
}) => {
  const [isValidated, setValidated] = useState(false)

  const useFormData = useForm({
    requestCategory,
    behavior: 'Select',
    scope: 'BusinessLocation'
  })

  const values = useFormData[0]

  useEffect(() => {
    const { requestCategory, behavior } = values

    if (requestCategory !== 'Unknown' && behavior !== 'Select') {
      setValidated(true)
    } else {
      setValidated(false)
    }
  }, [values])

  return (
    <ConfirmationDialog
      title={`Exclude ${itemValues.length} items`}
      positiveMessage="Apply exclusion"
      isButtonDisabled={!isValidated}
      onConfirm={() => handleConfirm(values)}
      onClose={handleClose}
    >
      <div className={styles.container}>
        <div className={styles.valuesToBeExcluded}>
          <p>Items to be excluded:</p>
          {itemValues.join(', ')}
        </div>
        <Select
          useForm={useFormData}
          label="Select scope"
          name="scope"
          defaultVal="businessLocation"
        >
          {Object.keys(scopeNames).map(k => (
            <option key={k} value={k}>
              {scopeNames[k]}
            </option>
          ))}
        </Select>
        <Select
          useForm={useFormData}
          label="Select behavior"
          name="behavior"
          defaultVal="Select"
        >
          {Object.keys(behaviorNames).map(k => (
            <option key={k} value={k}>
              {behaviorNames[k]}
            </option>
          ))}
        </Select>
      </div>
    </ConfirmationDialog>
  )
}

const PhoneNumberWithValidation = ({ pushFormattedPhoneNumber }) => {
  const [isValid, setIsValid] = useState(true)
  const [value, setValue] = useState('')

  function handleBlur(e) {
    const { value } = e.target

    dashboardApi.checkPhoneNumber(value).then(data => {
      if (data) {
        pushFormattedPhoneNumber(data)
        setValue(data)
        setIsValid(true)
      } else {
        setIsValid(false)
      }
    })
  }

  return (
    <TextInput
      label="Phone number"
      name="phone"
      onChangeValue={setValue}
      value={value}
      onBlur={handleBlur}
      valid={isValid}
    />
  )
}

const AddBlacklistRule = ({ handleConfirm, handleClose, isIndependent }) => {
  const [isValidated, setValidated] = useState(false)

  const [requestCategory, setRequestCategory] = useState('convertedPhoneNumber')

  const [itemValues, setItemValues] = useState([])

  const [behavior, setBehavior] = useState('noReach')

  const [scope, setScope] = useState('businessLocation')

  const [validatedPhoneNumbers, setValidatedPhoneNumbers] = useState([''])

  useEffect(() => {
    if (
      requestCategory === 'convertedPhoneNumber' &&
      validatedPhoneNumbers.length &&
      validatedPhoneNumbers.some(v => v)
    ) {
      setValidated(true)
    } else if (requestCategory !== 'Unknown' && itemValues.length) {
      setValidated(true)
    } else {
      setValidated(false)
    }
  }, [itemValues, requestCategory, validatedPhoneNumbers])

  function handleSubmit() {
    let valueArray

    if (requestCategory === 'convertedPhoneNumber') {
      valueArray = validatedPhoneNumbers.filter(v => v)
    } else {
      valueArray = itemValues
        .trim()
        .replace(/[\s,;\n]+/gm, '$')
        .split('$')
    }

    const reqArray = valueArray.map(itemValue => ({
      itemValue,
      requestCategory,
      behavior,
      scope
    }))

    handleConfirm(reqArray)
  }

  return (
    <ConfirmationDialog
      title="Add exclusion rule"
      positiveMessage="Apply exclusion"
      isButtonDisabled={!isValidated}
      onConfirm={handleSubmit}
      onClose={handleClose}
    >
      <div className={styles.container}>
        <Dropdown
          label="Select exclusion category"
          name="requestCategory"
          value={requestCategory}
          onChange={e => setRequestCategory(e.target.value)}
          options={blCats}
        ></Dropdown>
        <Dropdown
          label="Select scope"
          value={scope}
          onChange={e => setScope(e.target.value)}
          options={getOptions({ isIndependent, requestCategory })}
        ></Dropdown>
        <Dropdown
          label="Select behavior"
          name="behavior"
          defaultVal="Select"
          value={behavior}
          onChange={e => setBehavior(e.target.value)}
          options={behaviorNames}
        ></Dropdown>

        {requestCategory !== 'convertedPhoneNumber' && (
          <>
            <p className={styles.textareaLabel}>Values to exclude</p>
            <textarea
              className={styles.textarea}
              value={itemValues}
              onChange={e => setItemValues(e.target.value)}
              placeholder="add single value or multiple values seperated by space, comma or enter"
            ></textarea>
          </>
        )}
        {requestCategory === 'convertedPhoneNumber' && (
          <div className={styles.phoneNumbers}>
            {validatedPhoneNumbers.map((phoneNumber, i) => (
              <PhoneNumberWithValidation
                key={i}
                pushFormattedPhoneNumber={number =>
                  setValidatedPhoneNumbers(prev => {
                    const newArray = [...prev]
                    newArray[i] = number
                    return newArray
                  })
                }
              />
            ))}
            <button
              className={styles.addPhoneNumber}
              onClick={() => setValidatedPhoneNumbers(prev => [...prev, ''])}
            >
              <Plus /> Add another phone number
            </button>
          </div>
        )}
      </div>{' '}
    </ConfirmationDialog>
  )
}

const Select = ({ label, name, children, defaultVal, useForm }) => (
  <select onChange={useForm[1]} value={useForm[0][name]} name={name}>
    <option value={defaultVal} disabled>
      {label}
    </option>
    {children}
  </select>
)

export default AddBlacklistRule
